import React, { useCallback } from "react"
import { Layout, Table, Typography, Tag, Space, Button, Row, Col, Divider, Empty } from "antd"
import { StarOutlined, EyeOutlined, CloseOutlined, EnvironmentOutlined, BookOutlined, BankOutlined, ClockCircleOutlined, SafetyCertificateOutlined, CalendarOutlined, UserOutlined } from "@ant-design/icons"
import moment from "moment"

const { Text, Title } = Typography
const { Content } = Layout

// Define theme colors
const themeColors = {
  primary: '#52c41a',
  secondary: '#95de64', 
  tertiary: '#389e0d', 
  background: '#f6ffed',
  border: '#b7eb8f' 
}

const styles = {
  container: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#fff',
    padding: '24px'
  },
  header: {
    marginBottom: 24,
    borderBottom: `1px solid ${themeColors.border}`,
    paddingBottom: 16,
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 16,
  },
  headerStats: {
    display: "flex",
    gap: "24px",
    alignItems: "center",
  },
  statItem: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  candidateHeader: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    textAlign: "center",
  },
  actionIcons: {
    display: "flex",
    gap: "8px",
    justifyContent: "center",
    marginTop: "8px",
  },
  tableContainer: {
    flex: 1,
    overflow: 'auto'
  },
  icon: {
    marginRight: '8px',
    color: themeColors.primary
  },
  greenButton: {
    backgroundColor: themeColors.primary,
    borderColor: themeColors.primary,
    '&:hover': {
      backgroundColor: themeColors.tertiary,
      borderColor: themeColors.tertiary,
    }
  }
}

const getValueFromCandidate = (candidate, path, defaultValue = '-') => {
  if (!candidate) return defaultValue;
  
  if (candidate._original && path.startsWith('_original.')) {
    const parts = path.split('.').slice(1);
    let value = candidate._original;
    
    for (const part of parts) {
      if (!value || !value[part]) return defaultValue;
      value = value[part];
      
      if (Array.isArray(value) && value.length > 0) {
        value = value[0];
      }
    }
    
    return value || defaultValue;
  }
  
  const parts = path.split('.');
  let value = candidate;
  
  for (const part of parts) {
    if (!value || !value[part]) return defaultValue;
    value = value[part];
  }
  
  return value || defaultValue;
};

const parseLocation = (locationString) => {
  if (!locationString) return '-';
  
  const locationParts = locationString.split('-');
  if (locationParts.length >= 3) {
    const [country, state, city] = locationParts;
    return `${city}, ${state}, ${country}`;
  }
  return locationString;
};

const formatExperience = (months) => {
  if (!months) return "Not specified";
  
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;
  
  if (years > 0 && remainingMonths > 0) {
    return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  } else if (years > 0) {
    return `${years} year${years > 1 ? 's' : ''}`;
  } else {
    return `${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  
  try {
    if (dateStr.includes('/')) {
      const parts = dateStr.split('/');
      if (parts.length === 3) {
        const [year, month, day] = parts;
        return moment(`${month}/${day}/${year}`).format('LL');
      }
    }
    
    return moment(dateStr).format('LL');
  } catch (e) {
    return dateStr;
  }
};

const CompareView = ({ 
  candidates = [], 
  onClose, 
  onViewCandidate,
  onRemoveFromCompare
}) => {
  const handleRemove = useCallback((candidateId) => {
    onRemoveFromCompare?.(candidateId);
    if (candidates.length <= 2) {
      onClose?.();
    }
  }, [candidates.length, onClose, onRemoveFromCompare]);

  const comparisonFields = [
    {
      section: 'Basic Information',
      fields: [
        { 
          key: 'name', 
          label: 'Name',
          icon: <UserOutlined style={styles.icon} />,
          getValue: (candidate) => getValueFromCandidate(candidate, '_original.ContactName', candidate.name || '-')
        },
        { 
          key: 'location', 
          label: 'Location',
          icon: <EnvironmentOutlined style={styles.icon} />,
          getValue: (candidate) => {
            const locationString = getValueFromCandidate(candidate, '_original.HomeLocation');
            return parseLocation(locationString);
          }
        },
        { 
          key: 'resumeId', 
          label: 'Resume ID',
          getValue: (candidate) => getValueFromCandidate(candidate, '_original.ResumeID')
        },
        { 
          key: 'lastUpdate', 
          label: 'Last Updated',
          icon: <CalendarOutlined style={styles.icon} />,
          getValue: (candidate) => {
            const lastUpdate = getValueFromCandidate(candidate, '_original.LastUpdate');
            return formatDate(lastUpdate);
          }
        }
      ]
    },
    {
      section: 'Experience & Education',
      fields: [
        {
          key: 'experience',
          label: 'Total Experience',
          icon: <ClockCircleOutlined style={styles.icon} />,
          getValue: (candidate) => {
            const months = getValueFromCandidate(candidate, '_original.MonthsOfExperience');
            return formatExperience(parseInt(months));
          }
        },
        { 
          key: 'education', 
          label: 'Education Level',
          icon: <BookOutlined style={styles.icon} />,
          getValue: (candidate) => getValueFromCandidate(candidate, '_original.HighestDegree')
        }
      ]
    },
    {
      section: 'Current Position',
      fields: [
        {
          key: 'currentTitle',
          label: 'Current Title',
          icon: <BankOutlined style={styles.icon} />,
          getValue: (candidate) => getValueFromCandidate(candidate, '_original.RecentJobTitle')
        },
        {
          key: 'currentEmployer',
          label: 'Current Employer',
          getValue: (candidate) => getValueFromCandidate(candidate, '_original.RecentEmployer')
        },
        {
          key: 'currentPay',
          label: 'Current Salary',
          getValue: (candidate) => getValueFromCandidate(candidate, '_original.RecentPay')
        }
      ]
    },
    {
      section: 'Additional Information',
      fields: [
        {
          key: 'lastActivity',
          label: 'Last Activity',
          getValue: (candidate) => {
            const lastActivity = getValueFromCandidate(candidate, '_original.LastActivity');
            return lastActivity;
          }
        },
        {
          key: 'source',
          label: 'Source',
          getValue: (candidate) => candidate.source || 'CareerBuilder'
        }
      ]
    }
  ];

  // Transform data for Table component
  const dataSource = comparisonFields.flatMap(section => [
    { key: section.section, isSection: true, name: section.section },
    ...section.fields.map(field => ({
      key: field.key,
      name: field.label,
      icon: field.icon,
      ...candidates.reduce((acc, candidate, idx) => {
        const value = field.getValue(candidate);
        acc[`candidate${idx}`] = value;
        return acc;
      }, {})
    }))
  ]);

  const columns = [
    {
      title: "",
      dataIndex: "name",
      key: "name",
      width: 200,
      fixed: "left",
      render: (text, record) => {
        if (record.isSection) {
          return <Title level={5} style={{ color: themeColors.tertiary }}>{text}</Title>;
        }
        return (
          <div>
            {record.icon}
            <Text strong>{text}</Text>
          </div>
        );
      },
    },
    ...candidates.map((candidate, index) => ({
      title: () => (
        <div style={styles.candidateHeader}>
          <Title level={5} style={{ margin: '8px 0', color: themeColors.tertiary }}>
            {getValueFromCandidate(candidate, '_original.ContactName', candidate.name || 'Unnamed Candidate')}
          </Title>
          <Text type="secondary">
            {parseLocation(getValueFromCandidate(candidate, '_original.HomeLocation', candidate.location))}
          </Text>
          <div style={styles.actionIcons}>
            <Button
              type="text"
              icon={<EyeOutlined style={{ color: themeColors.primary }} />}
              onClick={(e) => {
                e.stopPropagation();
                onViewCandidate?.(candidate);
              }}
            />
            <Button
              type="text"
              icon={<CloseOutlined style={{ color: themeColors.primary }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleRemove(candidate.id);
              }}
            />
          </div>
        </div>
      ),
      dataIndex: `candidate${index}`,
      key: `candidate${index}`,
      width: 250,
    })),
  ];

  if (!candidates.length) {
    return (
      <Layout style={styles.container}>
        <div style={styles.header}>
          <Row justify="space-between" align="middle">
            <Col>
              <Title level={4} style={{ margin: 0, color: themeColors.tertiary }}>
                Candidate Comparison
              </Title>
            </Col>
            <Col>
              <Button 
                type="primary" 
                onClick={onClose}
                style={{ backgroundColor: themeColors.primary, borderColor: themeColors.primary }}
              >
                Back to Search Results
              </Button>
            </Col>
          </Row>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
          <Empty 
            description={
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                <Text>No candidates selected for comparison</Text>
                <Text type="secondary">Select candidates from the search results to compare them side by side</Text>
                <Button 
                  type="primary" 
                  onClick={onClose}
                  style={{ backgroundColor: themeColors.primary, borderColor: themeColors.primary }}
                >
                  Go to Search Results
                </Button>
              </div>
            } 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        </div>
      </Layout>
    );
  }

  return (
    <Layout style={styles.container}>
      <div style={styles.header}>
        <Row justify="space-between" align="middle">
          <Col>
            <Title level={4} style={{ margin: 0, color: themeColors.tertiary }}>
              Comparing {candidates.length} Candidates
            </Title>
          </Col>
          <Col>
            <Button 
              type="primary" 
              onClick={onClose}
              style={{ backgroundColor: themeColors.primary, borderColor: themeColors.primary }}
            >
              Back to Results
            </Button>
          </Col>
        </Row>
      </div>
      <div style={styles.tableContainer}>
        <Table
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          scroll={{ x: 'max-content', y: 'calc(100vh - 300px)' }}
          bordered
          size="middle"
          sticky
        />
      </div>
    </Layout>
  );
};

export default CompareView;

